import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, CheckCircle2, Home, Target, User, Mail, Phone, Building, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const Erstgespraech = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    topic: ''
  });

  // Generate next 30 days
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push(date);
      }
    }
    
    return dates;
  };

  const availableDates = generateDates();

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setIsLoadingSlots(true);
    const formattedDate = formatDateForAPI(date);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/appointments/available?date=${formattedDate}`
      );
      const data = await response.json();
      setAvailableSlots(data.available_slots || []);
    } catch (error) {
      toast.error('Fehler beim Laden der verfügbaren Zeiten');
      setAvailableSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const formatDateForAPI = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatDateDisplay = (date) => {
    const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    
    return `${days[date.getDay()]}, ${date.getDate()}. ${months[date.getMonth()]}`;
  };

  const handleTimeSelection = (time) => {
    setSelectedTime(time);
    // Scroll to contact form after time selection
    setTimeout(() => {
      const contactSection = document.getElementById('contact-form-section');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Bitte wählen Sie ein Datum und eine Uhrzeit');
      return;
    }

    if (!formData.name || !formData.email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          appointment_date: formatDateForAPI(selectedDate),
          appointment_time: selectedTime
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Terminanfrage erfolgreich gesendet!', {
          description: 'Sie erhalten in Kürze eine Bestätigung per E-Mail.'
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          topic: ''
        });
        setSelectedDate(null);
        setSelectedTime(null);
        
        // Google Ads Conversion Tracking
        if (typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion();
        }
      } else {
        toast.error(data.detail || 'Fehler beim Buchen des Termins');
      }
    } catch (error) {
      toast.error('Fehler beim Senden der Anfrage');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="page-container">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Home className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Erstgespräch buchen</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section-service">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="service-page-title">
            Kostenloses Erstgespräch vereinbaren
          </h1>
          <p className="service-page-subtitle">
            Buchen Sie direkt einen 1-stündigen Termin für eine kostenlose Erstanalyse Ihrer Prozesse. 
            Ich zeige Ihnen konkrete Einsparpotenziale auf – unverbindlich und transparent.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-5 w-5 text-accent" />
              <span>100% kostenlos</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-5 w-5 text-accent" />
              <span>Keine Verpflichtung</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-5 w-5 text-accent" />
              <span>1 Stunde Zeit für Sie</span>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="section-light">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Calendar Selection */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-accent" />
                  1. Wählen Sie ein Datum
                </h3>
                
                <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {availableDates.map((date, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedTime(null);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedDate && selectedDate.toDateString() === date.toDateString()
                          ? 'border-accent bg-accent/10 font-semibold'
                          : 'border-gray-200 hover:border-accent/50'
                      }`}
                    >
                      <div className="text-sm font-semibold">{formatDateDisplay(date)}</div>
                      <div className="text-xs text-gray-500">{date.getFullYear()}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Time Selection */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-accent" />
                  2. Wählen Sie eine Uhrzeit
                </h3>
                
                {!selectedDate ? (
                  <p className="text-gray-500 text-center py-8">
                    Bitte wählen Sie zuerst ein Datum
                  </p>
                ) : isLoadingSlots ? (
                  <p className="text-gray-500 text-center py-8">Lade verfügbare Zeiten...</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    Keine freien Termine an diesem Tag. Bitte wählen Sie ein anderes Datum.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {availableSlots.map((time) => (
                      <button
                        key={time}
                        onClick={() => handleTimeSelection(time)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-center ${
                          selectedTime === time
                            ? 'border-accent bg-accent/10 font-semibold'
                            : 'border-gray-200 hover:border-accent/50'
                        }`}
                      >
                        <div className="text-lg font-semibold">{time} Uhr</div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          {selectedDate && selectedTime && (
            <Card id="contact-form-section" className="mt-8">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-accent" />
                  3. Ihre Kontaktdaten
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                          placeholder="Max Mustermann"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        E-Mail <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                          placeholder="max@example.com"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Telefonnummer (optional)
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                          placeholder="+49 123 456789"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Firma (optional)
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                          placeholder="Musterfirma GmbH"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Thema / Wobei sollen wir sparen? (optional)
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                      <textarea
                        name="topic"
                        value={formData.topic}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                        placeholder="z.B. Prozessautomatisierung, Amazon-Optimierung, Buchhaltung..."
                      />
                    </div>
                  </div>

                  {/* Selected Summary */}
                  <div className="bg-accent/10 p-4 rounded-lg border-2 border-accent">
                    <p className="font-semibold text-lg mb-2">Ihr gewählter Termin:</p>
                    <p className="text-lg">
                      📅 {formatDateDisplay(selectedDate)}, {selectedDate.getFullYear()}
                    </p>
                    <p className="text-lg">🕐 {selectedTime} Uhr</p>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full cta-button-large"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Termin jetzt anfragen'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  
                  <p className="text-sm text-gray-500 text-center">
                    Nach dem Absenden erhalten Sie eine Bestätigung per E-Mail.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Erstgespraech;
